import { useState } from "react";
import StepNavigation from "../shared/StepNavigation";
import { FoerderFormData } from "./FoerderRechner";
import clsx from "clsx";
import { CheckSquare, Square } from "lucide-react";

interface Props {
  data: FoerderFormData;
  onChange: (data: Partial<FoerderFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

type MassnahmeValue =
  | "waermepumpe" | "biomasse" | "solarthermie"
  | "daemmung_dach" | "daemmung_fassade" | "fenster" | "lueftung"
  | "effizienzhaus" | "heizungsoptimierung";

interface MassnahmeOption {
  value: MassnahmeValue;
  label: string;
  description: string;
  foerdersatz: string;
}

const MASSNAHMEN_KATEGORIEN: { kategorie: string; info: string; optionen: MassnahmeOption[] }[] = [
  {
    kategorie: "Heizung",
    info: "KfW 458 – bis 70% Förderung",
    optionen: [
      { value: "waermepumpe", label: "Wärmepumpe", description: "Luft-, Wasser- oder Erdwärmepumpe", foerdersatz: "30–70%" },
      { value: "biomasse", label: "Biomasse-Heizung", description: "Pellet- oder Hackschnitzelheizung", foerdersatz: "30–35%" },
      { value: "solarthermie", label: "Solarthermie", description: "Solare Heizungsunterstützung", foerdersatz: "30–35%" },
    ],
  },
  {
    kategorie: "Gebäudehülle",
    info: "BAFA BEG EM – 15–20% Förderung",
    optionen: [
      { value: "daemmung_dach", label: "Dachdämmung", description: "Aufsparren-, Zwischensparren- oder Untersparrendämmung", foerdersatz: "15–20%" },
      { value: "daemmung_fassade", label: "Fassadendämmung", description: "Außenwanddämmung, WDVS", foerdersatz: "15–20%" },
      { value: "fenster", label: "Fenster & Türen", description: "Wärmedämmende Fenster, Haustür", foerdersatz: "15–20%" },
      { value: "lueftung", label: "Lüftungsanlage", description: "Kontrollierte Wohnraumlüftung mit WRG", foerdersatz: "15–20%" },
    ],
  },
  {
    kategorie: "Komplex­sanierung",
    info: "KfW 261 – bis 20% Tilgungszuschuss",
    optionen: [
      { value: "effizienzhaus", label: "KfW-Effizienzhaus", description: "Komplettsanierung zum KfW-Standard", foerdersatz: "5–20%" },
      { value: "heizungsoptimierung", label: "Heizungsoptimierung", description: "Hydraulischer Abgleich, Pumpenoptimierung", foerdersatz: "15–20%" },
    ],
  },
];


export default function Step2Massnahme({ data, onChange, onNext, onBack }: Props) {
  const [error, setError] = useState<string | undefined>();
  const selected = (data.massnahmen ?? []) as MassnahmeValue[];

  const toggle = (value: MassnahmeValue) => {
    setError(undefined);

    // Effizienzhaus schließt alle anderen aus
    if (value === "effizienzhaus") {
      onChange({ massnahmen: selected.includes("effizienzhaus") ? [] : ["effizienzhaus"] });
      return;
    }
    // Andere können nicht mit Effizienzhaus kombiniert werden
    if (selected.includes("effizienzhaus")) {
      onChange({ massnahmen: [value] });
      return;
    }

    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange({ massnahmen: next });
  };

  const isSelected = (v: MassnahmeValue) => selected.includes(v);

  const validate = () => {
    if (selected.length === 0) {
      setError("Bitte mindestens eine Maßnahme auswählen.");
      return false;
    }
    return true;
  };

  return (
    <div className="animate-slide-up" data-testid="step-2-massnahme">
      <p className="text-xs text-neko-gray mb-4">
        Mehrfachauswahl möglich – kombinierte Förderung wird automatisch berechnet.
      </p>

      {MASSNAHMEN_KATEGORIEN.map((kat) => (
        <div key={kat.kategorie} className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-neko-orange uppercase tracking-wider">
              {kat.kategorie}
            </p>
            <span className="text-xs text-neko-gray">{kat.info}</span>
          </div>
          <div className="space-y-2">
            {kat.optionen.map((opt) => {
              const aktiv = isSelected(opt.value);
              const disabled =
                opt.value !== "effizienzhaus" &&
                selected.includes("effizienzhaus");

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => !disabled && toggle(opt.value)}
                  disabled={disabled}
                  className={clsx(
                    "w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all",
                    aktiv
                      ? "border-neko-green bg-neko-green/10"
                      : "border-neko-dark-3 bg-neko-dark-2",
                    !disabled && !aktiv && "hover:border-neko-gray",
                    disabled && "opacity-40 cursor-not-allowed"
                  )}
                  data-testid={`massnahme-${opt.value}`}
                  aria-pressed={aktiv}
                >
                  <span className={clsx("mt-0.5 flex-shrink-0", aktiv ? "text-neko-green" : "text-neko-gray")}>
                    {aktiv
                      ? <CheckSquare className="w-4 h-4" />
                      : <Square className="w-4 h-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-neko-white">{opt.label}</span>
                      <span className={clsx(
                        "text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0",
                        aktiv ? "bg-neko-green/20 text-neko-green" : "bg-neko-green/10 text-neko-green/70"
                      )}>
                        {opt.foerdersatz}
                      </span>
                    </div>
                    <p className="text-xs text-neko-gray mt-0.5">{opt.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {selected.length > 1 && (
        <div className="bg-neko-orange/10 border border-neko-orange/20 rounded-lg px-3 py-2 mb-2 text-xs text-neko-orange">
          {selected.length} Maßnahmen ausgewählt – Förderprogramme werden kombiniert berechnet.
        </div>
      )}

      {error && <p className="text-xs text-neko-red mb-2" role="alert">{error}</p>}

      <StepNavigation
        onBack={onBack}
        onNext={() => { if (validate()) onNext(); }}
        nextLabel="Zu den Details"
      />
    </div>
  );
}
